import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const { FiArrowUp, FiArrowDown, FiChevronDown } = FiIcons;

// sortOrder is either "newest" or "oldest". "newest" is the default —
// matches the default chronological sort already used across the app.
const SortFilter = ({ sortOrder, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const options = [
    { id: "newest", label: "Newest to Oldest", icon: FiArrowDown },
    { id: "oldest", label: "Oldest to Newest", icon: FiArrowUp },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeOption = options.find((o) => o.id === sortOrder) || options[0];

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium border-white/10 text-gray-400 hover:text-white hover:border-white/20"
        style={{ background: "rgba(255,255,255,0.05)" }}
      >
        <SafeIcon icon={activeOption.icon} className="w-4 h-4" />
        <span>Sort</span>
        <SafeIcon
          icon={FiChevronDown}
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl z-50"
            style={{
              background: "#1a1a28",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(124,58,237,0.08)",
            }}
          >
            <div className="p-2">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    onSortChange(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg transition-all border text-sm text-left ${
                    sortOrder === option.id
                      ? "border-violet-500/40 bg-violet-900/20 text-white"
                      : "border-transparent text-gray-300 hover:bg-white/5"
                  }`}
                >
                  <SafeIcon icon={option.icon} className="w-4 h-4" />
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SortFilter;
